import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip, Paper, Box, Badge } from '@mui/material';
import { useMapEvents } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import { setModal } from '../../../store/reducers/helpersReducer';
import L from 'leaflet';

const TasksControlButton = ({ onClick }) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef(null);
    const dispatch = useDispatch();

    // Получаем информацию о несинхронизированных задачах
    const { tasks, tasksQueue } = useSelector(state => state.tasks);
    const user = useSelector(state => state.state.user)
    const hasPendingTasks = tasksQueue && tasksQueue.length > 0;
    const { newTasks, overdueTasks } = useMemo(() => {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        return {
            newTasks: tasks.filter(t => t.responsible_id === user.id && t.status === 'new')?.length || 0,
            overdueTasks: tasks.filter(t => t.responsible_id === user.id && !['done', 'cancelled'].includes(t.status) && new Date(t.deadline) < today)?.length || 0,
        }
    }, [tasks])
    console.log(tasks)

    const buttonPosition = useMemo(() => {
        const buttonRect = buttonRef.current?.getBoundingClientRect();
        return buttonRect ? {
            top: buttonRect.top,
            left: buttonRect.left,
            right: buttonRect.right,
            bottom: buttonRect.bottom,
            width: buttonRect.width,
            height: buttonRect.height
        } : null;
    }, [buttonRef.current]);

    useMapEvents({
        'modal:open': (e) => {
            if (e.modalType !== 'tasks') {
                setIsOpen(false);
            }
        },
        'modal:close': () => {
            setIsOpen(false);
        }
    });

    const handleOpenToggle = (newState) => {
        setIsOpen(newState);
        if (newState) {
            dispatch(setModal({ modalType: 'tasks', modalProps: { buttonPosition } }));
        } else {
            dispatch(setModal(null));
        }
    };

    useEffect(() => {
        if (buttonRef.current) {
            L.DomEvent.disableClickPropagation(buttonRef.current);
            L.DomEvent.disableScrollPropagation(buttonRef.current);
        }
    }, [buttonRef.current]);

    return createPortal(
        <Box sx={{ zIndex: 1202, display: 'flex', order: 1, pointerEvents: 'auto' }}>
            <Tooltip
                title={hasPendingTasks
                    ? `Задачи проекта (${tasksQueue.length} несинхронизированных)`
                    : 'Задачи проекта'
                }
                placement='right-start'
                arrow
            >
                <Paper
                    elevation={3}
                    ref={buttonRef}
                    sx={{
                        display: 'flex',
                        order: 4,
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: window.innerWidth < 768 ? 26 : 34,
                        height: window.innerWidth < 768 ? 26 : 34,
                        opacity: 0.9,
                        bgcolor: 'rgb(40, 40, 40)',
                        boxShadow: isOpen ? '0 0 3px 3px rgb(0, 174, 255)' : '0px 2px 6px grey',
                        color: 'rgb(0, 174, 255)',
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                        '&:hover': {
                            bgcolor: 'rgb(50, 50, 50)',
                            transform: 'scale(1.1)',
                        },
                    }}
                    onClick={() => handleOpenToggle(!isOpen)}
                >
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DashboardIcon sx={{ fontSize: window.innerWidth < 768 ? 22 : 24 }} />

                        {/* Индикатор несинхронизированных задач */}
                        {hasPendingTasks && (
                            <Box sx={{
                                position: 'absolute',
                                bottom: -10,
                                left: -10,
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                backgroundColor: '#ff9800',
                                border: '1px solid rgb(40, 40, 40)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                // animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                    '0%': { opacity: 1 },
                                    '50%': { opacity: 0.6 },
                                    '100%': { opacity: 1 }
                                }
                            }}>
                                <SyncProblemIcon sx={{
                                    fontSize: 14,
                                    color: '#333',
                                    // filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.8))'
                                }} />
                            </Box>
                        )}


                        {newTasks > 0 && (
                            <Badge
                                badgeContent={newTasks}
                                color="success"
                                max={999}
                                variant="standard"
                                sx={{
                                    position: 'absolute',
                                    top: window.innerWidth < 768 ? -2 : -4,
                                    right: window.innerWidth < 768 ? -2 : -4,
                                    '& .MuiBadge-badge': {
                                        color: 'white',
                                        backgroundColor: 'var(--color-green)',
                                        fontSize: window.innerWidth < 768 ? 9 : 12,
                                        padding: '3px',
                                        height: 'auto',
                                        width: 'auto',
                                    },
                                }}
                            />
                        )}


                        {overdueTasks > 0 && (
                            <Badge
                                badgeContent={overdueTasks}
                                color="secondary"
                                max={999}
                                variant="standard"
                                sx={{
                                    position: 'absolute',
                                    bottom: window.innerWidth < 768 ? -2 : -4,
                                    right: window.innerWidth < 768 ? -2 : -4,
                                    '& .MuiBadge-badge': {
                                        color: 'white',
                                        backgroundColor: 'var(--color-red)',
                                        fontSize: window.innerWidth < 768 ? 9 : 12,
                                        padding: '2px',
                                        width: 'auto',
                                        height: 'auto',
                                    }
                                }}
                            />
                        )}
                    </Box>
                </Paper>
            </Tooltip>
        </Box>,
        document.getElementById("map-controls-left-top-second-row")
    );
};

export default TasksControlButton; 